import type { Cadence } from '@selflevel/domain';

/**
 * EL CATÁLOGO DE ARRANQUE.
 *
 * La tesis del producto es que **un hábito es evidencia de una identidad, no una
 * tarea**, y la jerarquía va Área → Identidad → Meta → Hábito. Eso es verdad y
 * es lo que hace distinto a esto de un marcador de casillas — pero pedirle a
 * alguien que declare su identidad antes de dejarle marcar nada es la forma más
 * eficaz de que cierre la app.
 *
 * Así que el alta va al revés: se eligen dos o tres áreas, se pican hábitos de
 * una lista, y **la identidad se propone después**, cuando ya hay evidencia que
 * la sostenga. La frase que la app quiere poder decirle un año más tarde —
 * «dijiste: soy alguien que cuida su cuerpo · 203 días de evidencia» — necesita
 * los 203 días primero.
 */

export type AreaSuggestion = {
  key: string;
  name: string;
  marca: string;
  colorToken: string;
  /** La frase que se propondrá cuando haya evidencia. El usuario puede cambiarla. */
  identity: string;
  habits: readonly {
    name: string;
    marca: string;
    cadence: Cadence;
    target?: number;
    unit?: string;
  }[];
};

export const AREA_SUGGESTIONS: readonly AreaSuggestion[] = [
  {
    key: 'cuerpo',
    name: 'Cuerpo',
    marca: 'deporte',
    colorToken: '--dim-2',
    identity: 'soy alguien que cuida su cuerpo',
    habits: [
      { name: 'Moverme', marca: 'deporte', cadence: { kind: 'timesPerWeek', times: 3 } },
      { name: 'Caminar', marca: 'caminar', cadence: { kind: 'daily' } },
      { name: 'Fuerza', marca: 'fuerza', cadence: { kind: 'timesPerWeek', times: 2 } },
      { name: 'Beber agua', marca: 'agua', cadence: { kind: 'daily' }, target: 8, unit: 'vasos' },
      { name: 'Dormir bien', marca: 'sueno', cadence: { kind: 'daily' }, target: 7, unit: 'h' },
    ],
  },
  {
    key: 'mente',
    name: 'Mente',
    marca: 'meditacion',
    colorToken: '--dim-5',
    identity: 'soy alguien que cuida su cabeza',
    habits: [
      { name: 'Meditar', marca: 'meditacion', cadence: { kind: 'daily' }, target: 10, unit: 'min' },
      { name: 'Leer', marca: 'lectura', cadence: { kind: 'daily' }, target: 20, unit: 'páginas' },
      { name: 'Respirar', marca: 'respiracion', cadence: { kind: 'daily' } },
      { name: 'Sin pantalla al despertar', marca: 'pantalla', cadence: { kind: 'daily' } },
    ],
  },
  {
    key: 'trabajo',
    name: 'Laborar',
    marca: 'trabajo',
    colorToken: '--dim-4',
    identity: 'soy alguien que termina lo que empieza',
    habits: [
      {
        name: 'Bloque de foco',
        marca: 'tiempo',
        cadence: { kind: 'weekdays', days: [1, 2, 3, 4, 5] },
      },
      {
        name: 'Ordenar el día',
        marca: 'orden',
        cadence: { kind: 'weekdays', days: [1, 2, 3, 4, 5] },
      },
      { name: 'Estudiar', marca: 'estudio', cadence: { kind: 'timesPerWeek', times: 4 } },
      { name: 'Practicar idioma', marca: 'idiomas', cadence: { kind: 'daily' } },
    ],
  },
  {
    key: 'vinculos',
    name: 'Vínculos',
    marca: 'personas',
    colorToken: '--dim-6',
    identity: 'soy alguien que cuida a los suyos',
    habits: [
      { name: 'Llamar a alguien', marca: 'personas', cadence: { kind: 'timesPerWeek', times: 2 } },
      { name: 'Tiempo en casa', marca: 'casa', cadence: { kind: 'daily' } },
      { name: 'Salir con gente', marca: 'personas', cadence: { kind: 'timesPerWeek', times: 1 } },
    ],
  },
  {
    key: 'creacion',
    name: 'Creación',
    marca: 'creatividad',
    colorToken: '--dim-3',
    identity: 'soy alguien que hace cosas',
    habits: [
      { name: 'Escribir', marca: 'escritura', cadence: { kind: 'daily' } },
      { name: 'Tocar música', marca: 'musica', cadence: { kind: 'timesPerWeek', times: 3 } },
      {
        name: 'Proyecto propio',
        marca: 'creatividad',
        cadence: { kind: 'timesPerWeek', times: 2 },
      },
    ],
  },
  {
    key: 'orden',
    name: 'Orden',
    marca: 'orden',
    colorToken: '--dim-1',
    identity: 'soy alguien que tiene su vida en orden',
    habits: [
      { name: 'Cuentas al día', marca: 'dinero', cadence: { kind: 'timesPerWeek', times: 1 } },
      { name: 'Casa recogida', marca: 'casa', cadence: { kind: 'daily' } },
      { name: 'Medicación', marca: 'medicacion', cadence: { kind: 'daily' } },
      { name: 'Higiene', marca: 'higiene', cadence: { kind: 'daily' } },
    ],
  },
];

/** Los seis colores de área aprobados en el paso 3. No hay un séptimo. */
export const AREA_COLORS = ['--dim-1', '--dim-2', '--dim-3', '--dim-4', '--dim-5', '--dim-6'];

/** Las preguntas de la revisión. Cortas: una revisión larga no se hace. */
export const REVIEW_QUESTIONS = {
  weekly: [
    { key: 'funciono', label: '¿Qué funcionó esta semana?' },
    { key: 'estorbo', label: '¿Qué estorbó?' },
    { key: 'cambio', label: '¿Qué cambio hace la semana que viene más fácil?' },
  ],
  monthly: [
    { key: 'evidencia', label: '¿De qué hay evidencia este mes?' },
    { key: 'sobra', label: '¿Qué sobra?' },
    { key: 'sigue', label: '¿Qué sigue igual el mes que viene?' },
    { key: 'identidad', label: '¿Sigue siendo verdad lo que dijiste que eras?' },
  ],
} as const;

export const MOOD_LABELS = ['muy bajo', 'bajo', 'normal', 'bueno', 'muy bueno'] as const;
export const ENERGY_LABELS = ['agotado', 'flojo', 'normal', 'con energía', 'a tope'] as const;
